import { ThemeProvider } from '@mui/material/styles';
import { GridLocaleText } from '@mui/x-data-grid-pro';
import * as gridLocales from '@mui/x-data-grid-pro/locales';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import * as pickersLocales from '@mui/x-date-pickers/locales';
import { LocalizationProvider, LocalizationProviderProps } from '@mui/x-date-pickers-pro';
import { SupportedLanguageCode } from '@procureai/schemas';
import { createTheme, useLanguageCode } from '@procureai/ui-mui';
import * as dateFnsLocales from 'date-fns/locale';
import { PropsWithChildren, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { dateFnsLocaleFromLanguage } from '~/common/i18n';
import { useConfig } from '~/components/ConfigProvider';

type LocaleConfig = {
  grid: Partial<GridLocaleText>;
  pickers: Partial<NonNullable<LocalizationProviderProps<Date, any>['localeText']>>;
  dateFormats: LocalizationProviderProps<Date, any>['dateFormats'];
};

const localeConfigByLanguage = {
  en: {
    grid: gridLocales.enUS.components.MuiDataGrid.defaultProps.localeText,
    pickers: pickersLocales.enUS.components.MuiLocalizationProvider.defaultProps.localeText,
    dateFormats: { keyboardDate: 'dd/MM/yyyy' },
  },
  de: {
    grid: gridLocales.deDE.components.MuiDataGrid.defaultProps.localeText,
    pickers: pickersLocales.deDE.components.MuiLocalizationProvider.defaultProps.localeText,
    dateFormats: { keyboardDate: 'dd.MM.yyyy' },
  },
  pt: {
    grid: gridLocales.ptBR.components.MuiDataGrid.defaultProps.localeText,
    pickers: pickersLocales.ptBR.components.MuiLocalizationProvider.defaultProps.localeText,
    dateFormats: { keyboardDate: 'dd/MM/yyyy' },
  },
  es: {
    grid: gridLocales.esES.components.MuiDataGrid.defaultProps.localeText,
    pickers: pickersLocales.esES.components.MuiLocalizationProvider.defaultProps.localeText,
    dateFormats: { keyboardDate: 'dd.MM.yyyy' },
  },
  it: {
    grid: gridLocales.itIT.components.MuiDataGrid.defaultProps.localeText,
    pickers: pickersLocales.itIT.components.MuiLocalizationProvider.defaultProps.localeText,
    dateFormats: { keyboardDate: 'dd.MM.yyyy' },
  },
  fr: {
    grid: gridLocales.frFR.components.MuiDataGrid.defaultProps.localeText,
    pickers: pickersLocales.frFR.components.MuiLocalizationProvider.defaultProps.localeText,
    dateFormats: { keyboardDate: 'dd.MM.yyyy' },
  },
  pl: {
    grid: gridLocales.plPL.components.MuiDataGrid.defaultProps.localeText,
    pickers: pickersLocales.plPL.components.MuiLocalizationProvider.defaultProps.localeText,
    dateFormats: { keyboardDate: 'dd.MM.yyyy' },
  },
  fi: {
    grid: gridLocales.fiFI.components.MuiDataGrid.defaultProps.localeText,
    pickers: pickersLocales.fiFI.components.MuiLocalizationProvider.defaultProps.localeText,
    dateFormats: { keyboardDate: 'dd.MM.yyyy' },
  },
  ko: {
    grid: gridLocales.koKR.components.MuiDataGrid.defaultProps.localeText,
    pickers: pickersLocales.koKR.components.MuiLocalizationProvider.defaultProps.localeText,
    dateFormats: { keyboardDate: 'yyyy-MM-dd' },
  },
  nl: {
    grid: gridLocales.nlNL.components.MuiDataGrid.defaultProps.localeText,
    pickers: pickersLocales.nlNL.components.MuiLocalizationProvider.defaultProps.localeText,
    dateFormats: { keyboardDate: 'dd-MM-yyyy' },
  },
  ro: {
    grid: gridLocales.roRO.components.MuiDataGrid.defaultProps.localeText,
    pickers: pickersLocales.roRO.components.MuiLocalizationProvider.defaultProps.localeText,
    dateFormats: { keyboardDate: 'dd.MM.yyyy' },
  },
  th: {
    // MUI ships no Thai bundle for the grid or the pickers, so their own strings stay
    // English. Numbers, dates and the picker adapter still use th-TH / date-fns th.
    grid: gridLocales.enUS.components.MuiDataGrid.defaultProps.localeText,
    pickers: pickersLocales.enUS.components.MuiLocalizationProvider.defaultProps.localeText,
    dateFormats: { keyboardDate: 'dd/MM/yyyy' },
  },
  zh: {
    grid: gridLocales.zhCN.components.MuiDataGrid.defaultProps.localeText,
    pickers: pickersLocales.zhCN.components.MuiLocalizationProvider.defaultProps.localeText,
    dateFormats: { keyboardDate: 'yyyy-MM-dd' },
  },
};

/** Indexed with a plain string by the component below, hence the widened alias. */
const localeMap: Record<string, LocaleConfig> = localeConfigByLanguage;

// Fails to compile when a language is added to SUPPORTED_LANGUAGE_CODES upstream
// without a formatting locale here — see the same guard in common/i18n.
type UnmappedLocaleConfig = Exclude<SupportedLanguageCode, keyof typeof localeConfigByLanguage>;
const _everyLanguageHasALocaleConfig: UnmappedLocaleConfig extends never ? true : never = true;
void _everyLanguageHasALocaleConfig;

export const ThemeProviderConfigurable = ({ children }: PropsWithChildren) => {
  const { languageCode } = useLanguageCode();
  const { t: tDatePicker } = useTranslation(undefined, { keyPrefix: 'DatePicker' });
  const { palette } = useConfig();
  const localeConfig = localeMap[languageCode] || localeMap.de;
  const dateFnsLocal = dateFnsLocaleFromLanguage[languageCode] || dateFnsLocales.de;

  const themeMerged = useMemo(() => {
    const gridLocaleText: Partial<GridLocaleText> = localeConfig.grid;
    const pickersLocaleText: Partial<LocalizationProviderProps<Date, any>['localeText']> = {
      ...localeConfig.pickers,
      clearButtonLabel: tDatePicker('clearButton'),
      todayButtonLabel: tDatePicker('todayButton'),
      previousMonth: tDatePicker('previousMonth'),
      nextMonth: tDatePicker('nextMonth'),
    };
    const components: any = {
      MuiDataGrid: {
        defaultProps: {
          localeText: gridLocaleText,
        },
      },
      MuiLocalizationProvider: {
        defaultProps: {
          localeText: pickersLocaleText,
        },
      },
    };
    const themeOptions: Parameters<typeof createTheme>[0] = { components };
    if (palette) {
      themeOptions.palette = palette;
    }
    return createTheme(themeOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [palette, languageCode]);

  return (
    <ThemeProvider theme={themeMerged}>
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={dateFnsLocal}
        dateFormats={localeConfig.dateFormats}
      >
        {children}
      </LocalizationProvider>
    </ThemeProvider>
  );
};
